<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { storeToRefs } from 'pinia'

  import LoginForm from '~/components/login/Form.vue'
  import RegisterForm from '~/components/register/Form.vue'
  import ResetForm from '~/components/reset/Form.vue'
  import FaqPage from '~/pages/faq.vue'

  import { BngContentFit } from '../content-fit'
  import { BngHeaderLogo } from '../header-logo'
  import { BngMobileNavDropdown } from '../mobile-nav-dropdown'
  import { BngModal } from '../modal'
  import { BngNavMenu } from '../nav-menu'

  import BngCartMenu from './CartMenu.vue'
  import BngFavoritesMenu from './FavoritesMenu.vue'
  import BngProfileMenu from './ProfileMenu.vue'

  type Modals = 'login' | 'register' | 'faq' | 'contact' | 'reset' | 'navMenu'

  const router = useRouter()
  const { user } = storeToRefs(useUserStore())
  const { t } = useI18n()
  const { matches: matchDesktop } = useMatchMedia(ScreenWidth.desktop)

  const activeModal = computed(() => router.currentRoute.value.query.modal as Modals)

  function showModal(modal: Modals) {
    router.push({
      ...router.currentRoute.value,
      query: {
        ...router.currentRoute.value.query,
        modal,
      },
    })
  }
  function closeModal() {
    router.push({
      ...router.currentRoute.value,
      query: {
        ...router.currentRoute.value.query,
        modal: undefined,
      },
    })
  }
</script>
<template>
  <div class="lc-header">
    <BngContentFit>
      <div class="header-content">
        <div class="menu">
          <BngHeaderLogo />
          <button
            v-if="$config.public.isMobile"
            v-show="!matchDesktop"
            @click="() => showModal('navMenu')"
          >
            <SvgIcon
              icon="menu"
              :width="28"
              :height="18"
            />
          </button>
          <BngNavMenu
            v-show="matchDesktop"
            class="dark-row"
          />
        </div>
        <ClientOnly>
          <div class="shortcuts">
            <a
              href="javascript:void(0);"
              class="faq-button"
              @click="() => showModal('faq')"
            >
              <SvgIcon
                icon="help"
                :width="28"
                :height="28"
              />
              <span>FAQ</span>
            </a>
            <BngFavoritesMenu v-if="user" />
            <BngCartMenu />
            <BngProfileMenu v-if="user" />
            <a
              v-else
              href="javascript:void(0);"
              class="account-disconnected-button"
              @click="() => showModal('login')"
            >
              <SvgIcon
                icon="account"
                :width="28"
                :height="28"
              />
              <span>Connexion</span>
            </a>

            <div
              v-if="!user"
              class="button-group"
            >
              <Button
                type="secondary"
                @click="() => showModal('register')"
              >
                {{ t("navigation.register") }}
              </Button>
              <Button
                type="secondary"
                class="negative"
                @click="() => showModal('login')"
              >
                {{ t("navigation.login") }}
              </Button>
            </div>
          </div>
        </ClientOnly>
      </div>
    </BngContentFit>
    <ClientOnly>
      <Teleport to="#modal">
        <BngModal
          v-if="activeModal === 'contact'"
          :drawer="true"
          closable
          @close="() => closeModal()"
        >
          <ContactForm @close="() => closeModal()" />
        </BngModal>
        <BngModal
          v-else-if="activeModal === 'login'"
          closable
          @close="() => closeModal()"
        >
          <LoginForm
            @register="() => showModal('register')"
            @reset="() => showModal('reset')"
            @close="() => closeModal()"
          />
        </BngModal>
        <BngModal
          v-else-if="activeModal === 'register'"
          closable
          @close="() => closeModal()"
        >
          <RegisterForm
            @login="() => showModal('login')"
            @close="() => closeModal()"
          />
        </BngModal>
        <BngModal
          v-else-if="activeModal === 'reset'"
          closable
          @close="() => closeModal()"
        >
          <ResetForm @close="() => closeModal()" />
        </BngModal>
        <BngModal
          v-else-if="activeModal === 'faq'"
          drawer
          closable
          @close="() => closeModal()"
        >
          <FaqPage @close="() => closeModal()" />
        </BngModal>
        <BngModal
          v-else-if="activeModal === 'navMenu'"
          closable
          @close="() => closeModal()"
        >
          <BngMobileNavDropdown
            :disconnected="!user"
          />
        </BngModal>
      </Teleport>
    </ClientOnly>
  </div>
</template>
<style scoped>
.lc-header {
  position: relative;
  z-index: 40;
  display: flex;
  height: 62px;
  flex: 0 0 auto;
  justify-content: center;
  background-color: var(--color-secondary);

  .header-content {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    flex-direction: row;
    justify-content: space-between;

    .c-header-logo {
      margin-right: 1em;
    }

    .menu {
      display: flex;
      flex-direction: row;

      .c-icon {
        padding: 0.675em 0;
        color: white;
      }
    }

    .shortcuts {
      position: relative;
      display: flex;
      min-height: 100%;
      flex: 0 1;
      flex-direction: row;
      align-items: center;
      justify-content: center;

      .c-icon {
        color: white;
        cursor: pointer;
      }

      > a,
      > button {
        position: relative;
        display: flex;
        height: 100%;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 0 0.25em;

        &::before {
          position: absolute;
          border-width: 0 8px 6px;
          border-style: solid;
          border-right-color: transparent;
          border-bottom-color: transparent;
          border-left-color: transparent;
          content: "";
          transition: border-width ease-out 0.15s;
        }

        &.active,
        &:hover,
        &.NuxtLink-active {
          &::before {
            bottom: 0;
            border-width: 0 8px 6px;
            border-bottom-color: white;
          }
        }

        .button-content {
          display: flex;
          flex-direction: column;

          .button-text {
            padding-top: 0.25rem;
            color: white;
            font-family: Open-Sans, sans-serif;
            font-size: 0.75em;
            font-weight: 600;
          }
        }
      }

      .account-disconnected-button,
      .faq-button {
        width: 4em;
        padding-top: 0.375em;

        span {
          color: white;
          font-family: Open-Sans, sans-serif;
          font-size: 0.75em;
          font-weight: 600;
          white-space: nowrap;
        }
      }

      .button-group {
        display: none;
        flex-direction: row;

        .c-button {
          margin-left: 0.5rem;
          font-size: 1.125em;
          letter-spacing: 1.5px;
        }
      }

      .c-favorites-menu {
        display: none;
      }
    }
  }

  @media screen and (width >= 1024px) {
    height: 90px;

    .header-content {
      .shortcuts {
        .account-disconnected-button {
          display: none;
        }

        .button-group {
          display: flex;
        }

        > a,
        > button {
          &:not(:first-child) {
            margin-left: 1.125em;
          }
        }

        .faq-button {
          display: none;
        }

        .c-favorites-menu {
          display: flex;
        }
      }
    }
  }
}
</style>
